import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import { useNarrowerThan } from '@lib/hooks/sizing/element-size';
import { describeScheduleError, type SocialPost } from '@lib/social/scheduling/posts';
import formCss from '@lib/style/form.module.scss';
import { showToast } from '@lib/utils/toastmaster';
import { WarningAmberRounded } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { socialConnectionsStore } from '@stores/SocialConnections';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyStatusConflict } from '../postConflict';
import scheduleCss from '../SchedulePage.module.scss';
import { schedulePath } from '../schedulePath';
import { AccountCard } from './AccountCard';
import { ConnectAccountCard } from './ConnectAccountCard';
import { MediaPane } from './MediaPane';
import css from './PostForm.module.scss';
import { isStatusConflict, PostFormStore, usePostForm } from './PostFormStore';
import { PublishTimeField } from './PublishTimeField';

/**
 * Submit the form and put every failure in front of the user: a preflight
 * refusal is already on the accounts (the store parsed it), a status
 * conflict reloads the post, anything else is a toast. Null when nothing was
 * saved. Shared by the Save button and by "Save and retry".
 */
export const savePost = async (form: PostFormStore): Promise<SocialPost[] | null> => {
  // Read before the await: once the save lands, the form adopts the stored post and nothing is pending.
  const edit = form.pendingEdit;
  try {
    const posts = await form.submit();
    if (form.mode.kind === 'edit') {
      trackingStore.schedulePostEdited({
        status: posts[0].status,
        changed:
          edit?.publishAt !== null && edit?.content ? 'both' : edit?.content ? 'content' : 'time',
      });
    }
    return posts;
  } catch (error) {
    // The store parsed the refusal into `violations`; a refusal that named no account still gets the toast.
    if (form.violations.size > 0) return null;
    if (form.mode.kind === 'edit' && isStatusConflict(error)) {
      await notifyStatusConflict(form.mode.postId);
      return null;
    }
    showToast('social-post-action-failed', {
      action: form.mode.kind === 'create' ? 'schedule' : 'save',
      description: describeScheduleError(error),
      error,
    });
    return null;
  }
};

/**
 * Below this width the media folds above the form instead of taking a column
 * beside it. Measured on the form's frame, not the viewport: the nav rail
 * changes how much room the page gets (see `useNarrowerThan`).
 */
const STACK_BELOW_PX = 960;

/**
 * The form body shared by the create and post pages: the render's media,
 * the accounts with their fields, when, and the action row. The
 * page decides where to go once the API answers, and fills the slots with
 * what only it knows: the post's status line and its cancel and retry.
 */
export const PostForm = observer(function PostForm({
  now,
  intro,
  leadingActions,
  trailingActions,
}: {
  now: Date;
  /** Above the fields, in the form column. */
  intro?: ReactNode;
  /** Start of the action row, left of Save. */
  leadingActions?: ReactNode;
  /** End of the action row, right of Save; the primary when the post is retryable. */
  trailingActions?: ReactNode;
}) {
  const form = usePostForm();
  const navigate = useNavigate();
  // The frame's width is nothing inside it can change, so the measurement cannot feed back on itself.
  const [frameRef, stacked] = useNarrowerThan<HTMLDivElement>(STACK_BELOW_PX);
  const { render } = form;
  const portrait =
    render !== null &&
    render.width !== null &&
    render.height !== null &&
    render.height > render.width;
  const isCreate = form.mode.kind === 'create';
  const connections = socialConnectionsStore.data;
  const post = form.post;

  const accounts: typeof connections = isCreate
    ? connections
    : post && connections
      ? connections.filter((c) => c.id === post.connectionId)
      : null;

  const handleSubmit = async () => {
    const posts = await savePost(form);
    if (!posts) return;
    if (form.mode.kind === 'create') {
      trackingStore.schedulePostCreated({
        targets: posts.length,
        // Through the summary: which field means "unpublished" is each platform's own (YouTube has none).
        drafts: form.targets.filter((t) => t.module.describeContent(t.content).draft).length,
        platforms: [...new Set(form.targets.map((t) => t.module.platform))].sort(),
        when: form.when === 'now' ? 'now' : 'later',
      });
      showToast('social-post-scheduled', { count: posts.length, now: form.when === 'now' });
    } else {
      showToast('social-post-saved');
    }
    navigate(schedulePath(posts[0].publishAt, now));
  };

  return (
    <div
      ref={frameRef}
      className={classNames(css.frame, stacked !== false && css.stacked, portrait && css.portrait)}
    >
      <div className={css.layout}>
        <MediaPane stacked={stacked} portrait={portrait} />
        <div className={css.form}>
          {intro}

          <section className={css.section}>
            <h3 className={css.sectionTitle}>{isCreate ? 'Post to' : 'Account'}</h3>
            {accounts === null && socialConnectionsStore.error ? (
              // The list failed, which is not "no accounts": say so and offer the fetch again.
              <div className={scheduleCss.stateRow}>
                <span>Couldn’t load your accounts.</span>
                <ActionButton
                  variant="secondary"
                  onClick={() => socialConnectionsStore.update().catch(() => undefined)}
                >
                  Retry
                </ActionButton>
              </div>
            ) : accounts === null ? (
              <Skeleton
                variant="rounded"
                height={72}
                sx={{ borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.04)' }}
              />
            ) : accounts.length === 0 && !isCreate ? (
              <span className={css.sectionHint}>This account is no longer connected.</span>
            ) : (
              <div className={css.accounts}>
                {accounts.map((connection) => (
                  <AccountCard key={connection.id} connection={connection} selectable={isCreate} />
                ))}
                {isCreate && <ConnectAccountCard first={accounts.length === 0} />}
              </div>
            )}
          </section>

          <section className={css.section}>
            <PublishTimeField now={now} />
          </section>

          {!form.readOnly && (
            <div className={css.submitRow}>
              {form.blockers.length > 0 && (
                <div className={classNames(formCss.warningBox, css.blockers)} role="status">
                  <WarningAmberRounded className={formCss.warningIcon} />
                  <div className={formCss.warningBody}>
                    <span className={formCss.warningTitle}>
                      Before you can {form.when === 'now' ? 'post' : isCreate ? 'schedule' : 'save'}
                    </span>
                    <ul className={css.blockersList}>
                      {form.blockers.map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className={css.actionRow}>
                {leadingActions && <div className={css.leading}>{leadingActions}</div>}
                {/* One primary per row: on a retryable post that is Retry, the trailing slot. */}
                <LoadingActionButton
                  variant={form.retryable ? 'secondary' : 'primary'}
                  disabled={!form.canSubmit || form.render?.failed === true}
                  loadingText={isCreate ? 'Scheduling…' : 'Saving…'}
                  onClick={handleSubmit}
                >
                  {form.when === 'now' ? 'Post now' : isCreate ? 'Schedule' : 'Save changes'}
                </LoadingActionButton>
                {trailingActions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
